import { useState, useEffect, useRef } from "react";
import { Box, FormControl, styled, Autocomplete, TextField,
  Accordion, AccordionDetails, AccordionSummary, Checkbox, 
  Typography} from "@mui/material";
import FormControlLabel from '@mui/material/FormControlLabel';
import ExternalLaboratory from "../../../api/Admin/ExternalLaboratory";
import ShippingAddress from "../../../api/WorkRequest/ShippingAddress";
import ModalSimpleButton from '../../../components/Modal/ModalSimpleButton';
import ExternalLaboratoryForm from "./ExternalLaboratoryForm";
import ExternalLaboratoryShippingForm from "./ExternalLaboratoryShippingForm"
import { GlobalButton, GlobalSecondaryButton } from "../../styles";
import { useAuth } from "../../../context/AuthContext";
import { Roles } from "../../../constants/roles";


const StyledBox = styled(Box)(() => ({
  display: 'flex',
  marginBottom: "20px"
}));

const initialState = {
  laboratoryName: '',
  labCode: '',
  shippingAddressID: null,
  isActive: false,
  labContactEmailAddress: '',
  onSiteContactEmailAddress: '',
  notes: '',
  shippingAddress: null
};

const ExternalLaboratoryManagement = () => {
  const [isNewExternalLab, setIsNewExternalLab] = useState(false);
  const [externalLabData, setExternalLabData] = useState(initialState);
  const [externalLabList, setExternalLabList] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const [showInactiveExternalLabs, setShowInactiveExternalLabs] = useState(false);
  const [onSiteContactEmailError, setOnSiteContactEmailError] = useState(false);

  const [modalSimpleButton, setModalSimpleButton] = useState(false);
  const [modalSimpleButtonText, setModalSimpleButtonText] = useState('');
  const [modalSimpleTitle, setModalSimpleTitle] = useState('');
  const [modalSimpleText, setModalSimpleText] = useState('');

  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole([Roles.AnalyticalSME, Roles.Developer]);

  const formikLabRef = useRef();
  const formikShippingRef = useRef();
  const filteredExternalLabs = externalLabList?.filter(f => showInactiveExternalLabs || f.isActive) ?? [];
  const isDisplayForm = externalLabData?.laboratoryName || isNewExternalLab;

  const resetData = () => setExternalLabData(initialState);

  useEffect(() => {
    loadAllExternalLabs();
  }, []);

  function loadAllExternalLabs() {
    ExternalLaboratory.getAll().then((res) => {
      if (res.length > 0) {

        let array = [];
        array.push({ laboratoryName: "" });
        array.push(...res);

        setExternalLabList(array.sort(
          (a,b) => a.laboratoryName.localeCompare(b.laboratoryName)
        ));
      }
    }); 
  }

  function handleExternalLabSelection(externalLab) {
    resetData();
    
    if (externalLab?.laboratoryName) {
      setIsNewExternalLab(false);
      setExternalLabData(externalLab);
    }
  }

  function handleNewExternalLab() {
    setIsNewExternalLab(true);
    resetData();
  }
  
  const validateFormik = async(formikRef) => {
    if (!formikRef.current){ 
      return true;
    }
    const errors = await formikRef.current.validateForm();
    const touchedFields = Object.keys(errors).reduce((acc, key) => {
      acc[key] = true;
      return acc;
      }, {}
    );
    formikRef.current.setTouched(touchedFields);
    return (Object.keys(errors).length === 0);
  }

  const vaidateAll = async() => {
    const isValidLab = await validateFormik(formikLabRef);
    const isValidShipping = await validateFormik(formikShippingRef);
    return isValidLab && isValidShipping && !onSiteContactEmailError;
  }

  const handleSubmit = async(event) => {
    event.preventDefault();

    if (! await vaidateAll()) {
      return;
    }

    setIsSaving(true);

    const labValues = formikLabRef.current.values;
    const shippingValues = formikShippingRef.current ? formikShippingRef.current.values : null;

    const updatedShippingValues = externalLabData.shippingAddress !== null && shippingValues !== null ? {
      ...externalLabData.shippingAddress,
      recipientName: shippingValues.recipientName,
      company: shippingValues.company,
      department: shippingValues.department,
      address1: shippingValues.address1,
      city: shippingValues.city,
      state: shippingValues.state,
      country: shippingValues.country,
      postalCode: shippingValues.postalCode,
      phoneNumber1: shippingValues.phoneNumber1,
      emailAddress: shippingValues.emailAddress,
    } : null;

    const updatedExternalLabData = {
      ...externalLabData,
      laboratoryName: labValues.laboratoryName,
      labCode: labValues.labCode,
      isActive: labValues.isActive,
      labContactEmailAddress: labValues.labContactEmailAddress,
      onSiteContactEmailAddress: labValues.onSiteContactEmailAddress,
      notes: labValues.notes,
      shippingAddress: updatedShippingValues
    };

    try {
      const apiAction = isNewExternalLab
        ? ExternalLaboratory.add(updatedExternalLabData)
        : ExternalLaboratory.update(updatedExternalLabData.laboratoryName, updatedExternalLabData);

      await apiAction;

      resetData();
      openModalSimpleButton('Success', 'External Laboratory saved successfully!', 'Ok');
      setIsNewExternalLab(false);
      loadAllExternalLabs();

    } catch (error) {
      openModalSimpleButton('Fail', `Error saving External Laboratory : ${error?.message}`, 'Ok');
    } finally {
      setIsSaving(false);
    } 
  }

  function closeModalSimpleButton() {
    setModalSimpleButton(false);
  }

  function openModalSimpleButton(title, text, buttonText) {
    setModalSimpleButtonText(buttonText)
    setModalSimpleButton(true);
    setModalSimpleTitle(title);
    setModalSimpleText(text);
  }

  function toggleShowInactiveExternalLabs() {
    const showInactive = !showInactiveExternalLabs;
    setShowInactiveExternalLabs(showInactive);

    if (!isNewExternalLab && !showInactive && externalLabData?.isActive === false) {
      resetData();
    }
  }

  return (
    <StyledBox flexDirection="column" justifyContent="space-between">

      <FormControlLabel sx={{ marginLeft: "5px", marginBottom: "5px"}}
        control={
          <Checkbox
            checked = {showInactiveExternalLabs}
            onChange={() => toggleShowInactiveExternalLabs()}
          />}
        label="Show inactive External Laboratories"         
      />

      <StyledBox>
        <FormControl sx={{ m: 1, width: "44rem", display: "flex" }} variant='outlined'>
          <StyledBox sx={{ justifyContent: "space-between" }}>
            <Autocomplete
              noOptionsText={"Loading External Laboratories..."}
              id="external_labs-select"
              value={externalLabData}
              onChange={(e, value) => { handleExternalLabSelection(value) }}
              disablePortal
              options={filteredExternalLabs ?? []}
              getOptionLabel={(option) => option?.laboratoryName ?? ""}
              sx={{ width: "35rem" }}
              renderInput={(params) => <TextField {...params} variant="outlined" size="small" margin="none" label="External Laboratory"/> }
              autoSelect
            />

            { canEdit && <GlobalSecondaryButton sx={{width:"8rem", marginLeft:"1rem" }}
                variant='contained'
                type="button"
                onClick={handleNewExternalLab}
              >Add New</GlobalSecondaryButton>
            }

          </StyledBox>
        </FormControl>
      </StyledBox>
            
      { isDisplayForm && <ExternalLaboratoryForm
          externalLabData={externalLabData} 
          formikLabRef={formikLabRef}
          invalidExternalLabNames={externalLabList.filter(s => s.laboratoryName !== externalLabData?.laboratoryName).map(s => s.laboratoryName)}
          invalidLabCodes={externalLabList.filter(s => s.labCode !== externalLabData?.labCode).map(s => s.labCode)}
          isNewExternalLab={isNewExternalLab}
          canEdit={canEdit}
          onSiteContactEmailError={onSiteContactEmailError}
          setOnSiteContactEmailError={setOnSiteContactEmailError}
        />
      }

      { isDisplayForm && externalLabData?.shippingAddress === null && <Box
         sx={{ marginTop: 2, marginBottom:2 }}>
          <GlobalSecondaryButton
            variant="contained"
            type="button"
            onClick={() => 
              setExternalLabData(prev => ({
                ...prev,
                shippingAddress : new ShippingAddress({})
              }))
            }
            disabled={!canEdit}>
            Add Shipping Address
          </GlobalSecondaryButton>
        </Box>
      }

      { isDisplayForm && externalLabData?.shippingAddress && <Box
          sx={{ width: '65rem', padding: 1, marginTop: 2, marginBottom:2 }}
        >
          <Accordion expanded={true}>
            <AccordionSummary
              aria-controls="information-content"
              id="information-header"
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", width: "60rem"}}>
                <Typography variant="h5"> Shipping Address </Typography>
                <GlobalSecondaryButton
                  variant="contained"
                  type="button"
                  onClick={() => 
                    setExternalLabData(prev => ({
                      ...prev,
                      shippingAddress : null
                    }))
                  }
                  disabled={!canEdit || externalLabData.shippingAddressID !== null }>
                  Delete
                </GlobalSecondaryButton>
              </Box>
            </AccordionSummary>
  
            <AccordionDetails>
              <ExternalLaboratoryShippingForm
                shippingAddressData={externalLabData.shippingAddress}
                formikShippingRef={formikShippingRef}
                isNewExternalLab={isNewExternalLab}
                canEdit={canEdit}
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      }

      <form onSubmit={handleSubmit}>
        { canEdit && isDisplayForm && <StyledBox 
          sx={{ mb: 1, mt: 2, justifyContent: "space-between" }}>
            <GlobalButton sx={{ width: "8rem" }}
              variant='contained'             
              type="submit"
              disabled={isSaving}
            >Save</GlobalButton>
          </StyledBox>
        }
      </form >  

      <ModalSimpleButton 
        title={modalSimpleTitle} 
        buttonText={modalSimpleButtonText} 
        buttonAction={closeModalSimpleButton} 
        open={modalSimpleButton} 
        setOpen={setModalSimpleButton}
      >
        <label>
          {modalSimpleText}
        </label>
      </ModalSimpleButton>

    </StyledBox>
  );
};

export default ExternalLaboratoryManagement;
